import React, { useState, useEffect } from 'react';
import './Toast.css';

/**
 * Toast notification component.
 *
 * @param {string} message - Message to display
 * @param {string} type - 'success' | 'error' | 'info'
 * @param {number} duration - Auto-dismiss duration in ms (default 3000, 5000 with action)
 * @param {function} onClose - Callback when toast closes
 * @param {string} actionLabel - Optional action link text
 * @param {function} onAction - Optional action callback
 */
export function Toast({ message, type = 'info', duration, onClose, actionLabel, onAction }) {
  const [visible, setVisible] = useState(true);

  // Default duration: 5000ms if action is present, 3000ms otherwise
  const effectiveDuration = duration || (actionLabel && onAction ? 5000 : 3000);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      if (onClose) {
        setTimeout(onClose, 300); // Wait for fade animation
      }
    }, effectiveDuration);

    return () => clearTimeout(timer);
  }, [effectiveDuration, onClose]);

  if (!message) return null;

  const handleAction = () => {
    if (onAction) {
      onAction();
      setVisible(false);
      if (onClose) setTimeout(onClose, 300);
    }
  };

  return (
    <div className={`toast toast--${type} ${visible ? 'toast--visible' : 'toast--hidden'}`}>
      <span className="toast__icon">
        {type === 'success' && '✓'}
        {type === 'error' && '✗'}
        {type === 'info' && 'ℹ'}
      </span>
      <span className="toast__message">
        {message}
        {actionLabel && onAction && (
          <>
            {' — '}
            <button className="toast__action" onClick={handleAction}>
              {actionLabel}
            </button>
          </>
        )}
      </span>
      <button className="toast__close" onClick={() => {
        setVisible(false);
        if (onClose) setTimeout(onClose, 300);
      }}>
        ×
      </button>
    </div>
  );
}

export default Toast;
